import type { OrganizationBranding } from '../types/access'

/**
 * Matches internal uploaded branding asset routes such as
 *   /api/orgs/<orgId>/branding/logo/file
 *   /api/orgs/<orgId>/branding/banner/file
 * These are relative paths resolved against the active API origin — valid
 * branding references, but never valid external URLs.
 */
const INTERNAL_BRANDING_ASSET_ROUTE = /^\/api\/orgs\/[A-Za-z0-9_-]{1,64}\/branding\/(?:logo|banner)\/file$/

/** True when `value` is an internal uploaded branding asset route. */
export const isInternalBrandingAssetRoute = (value?: string | null): boolean => (
  typeof value === 'string' && INTERNAL_BRANDING_ASSET_ROUTE.test(value.trim())
)

/** True when `value` is a valid external branding URL (absolute http/https). */
export const isValidExternalBrandingUrl = (value?: string | null): boolean => {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!trimmed) return false
  try {
    return ['http:', 'https:'].includes(new URL(trimmed).protocol)
  } catch {
    return false
  }
}

/**
 * Normalizes a branding URL entered/echoed in the editable *external URL* field.
 * Internal uploaded-asset routes and blanks collapse to null so they are never
 * submitted as a manual external URL override.
 */
export const normalizeBrandingUrlInput = (value?: string | null): string | null => {
  const trimmed = value?.trim() || ''
  if (!trimmed) return null
  if (isInternalBrandingAssetRoute(trimmed)) return null
  return trimmed
}

/**
 * Normalized brand context derived from OrganizationBranding.
 * All fields are optional — missing fields should fall back gracefully in the UI.
 *
 * Note: AccessContext applies brand colors to CSS custom properties on
 * document.body (not document.documentElement — body.theme-dark redeclares
 * several of these vars directly, which would otherwise win the cascade over
 * an inline override sitting on the <html> ancestor). The derived CSS
 * variables are:
 *   --brand-primary, --brand-secondary, --brand-accent
 *   --brand-surface       (~8% primary tint  — subtle card/badge tint)
 *   --brand-surface-muted (~4% primary tint  — page background tint)
 *   --brand-nav           (~13% primary tint — nav background)
 *   --brand-button        (same as primary   — button fill)
 *   --brand-button-text   (contrast-checked black/white, or branding's buttonText if it passes)
 *   --brand-focus         (~25% primary tint — focus ring)
 *   --link                (brand primary, only if it passes WCAG AA contrast
 *                          against --surface for the active theme; otherwise
 *                          left unset so the safe stylesheet default applies)
 *
 * AccessContext ALSO overrides base vars when brand is active:
 *   --primary             → brand primary (propagates to all existing CSS)
 *   --primary-strong      → brand secondary (hover/strong states)
 *   --surface-subtle      → ~8% primary tint (hover rows, subtle areas)
 *
 * --primary/--primary-strong are used for backgrounds and borders, where any
 * color is safe. Anywhere brand color is used as foreground *text*, prefer
 * var(--link) instead — it's contrast-validated per theme.
 */
export type BrandingContext = {
  displayName?: string
  logoUrl?: string
  bannerUrl?: string
  primaryColor?: string
  secondaryColor?: string
  accentColor?: string
  /** Derived: ~13% tint of nav base color, used for nav background */
  navColor?: string
  /** Derived: primary color, used for button fills */
  buttonColor?: string
  /** Derived: ~25% tint of primary, used for focus rings */
  focusColor?: string
  /** Derived: ~8% tint of primary, used for card/surface tints */
  surfaceColor?: string
  /** Derived: ~4% tint of primary, used for page background tints */
  surfaceMutedColor?: string
}

export const normalizeBranding = (branding?: OrganizationBranding | null): BrandingContext => {
  if (!branding) return {}
  const primary = branding.primaryColor || branding.primary_color || undefined
  const secondary = branding.secondaryColor || branding.secondary_color || undefined
  const accent = branding.accentColor || branding.accent_color || undefined
  const logo = branding.logoUrl || branding.logo_url || branding.logoAssetUrl || branding.logo_asset_url || undefined
  const banner = branding.bannerUrl || branding.banner_url || branding.bannerAssetUrl || branding.banner_asset_url || undefined
  const navBase = secondary || primary
  return {
    displayName: branding.displayName || branding.display_name || undefined,
    logoUrl: logo,
    bannerUrl: banner,
    primaryColor: primary,
    secondaryColor: secondary,
    accentColor: accent,
    navColor: navBase ? `${navBase}22` : undefined,
    buttonColor: primary,
    focusColor: primary ? `${primary}40` : undefined,
    surfaceColor: primary ? `${primary}14` : undefined,
    surfaceMutedColor: primary ? `${primary}0a` : undefined,
  }
}
